import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, throwApiError } from "@/lib/utils.server";
import { isForbiddenError } from "@/lib/utils";
import { getDivergenceReports, getDivergenceReport } from "@/lib/mma";
import {
  GetDivergenceReportsResponse,
  GetDivergenceReportResponse,
  DivergenceReportReadable,
} from "@/lib/mma/types.gen";

// Fetches all divergence reports
export const getDivergenceReportsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { from: string; to: string }) => data)
  .handler(async ({ data }): Promise<GetDivergenceReportsResponse["data"]> => {
    const config = await createAuthenticatedApiConfig();
    const { from, to } = data;

    // Parse dates and convert to Unix timestamps
    const fromDate = from ? new Date(from) : new Date();
    const toDate = to ? new Date(to) : new Date();

    // Validate dates
    const fromTimestamp = isNaN(fromDate.getTime())
      ? Math.floor(Date.now() / 1000) - 30 * 24 * 60 * 60
      : Math.floor(fromDate.getTime() / 1000);
    const toTimestamp = isNaN(toDate.getTime()) ? Math.floor(Date.now() / 1000) : Math.floor(toDate.getTime() / 1000);

    const response = await getDivergenceReports({
      ...config,
      query: {
        after: fromTimestamp,
        before: toTimestamp,
      },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get divergence reports", response.response?.status);
    }
    return response.data?.data || [];
  });

interface GetDivergenceReportFnParams {
  id: string;
}

// Fetches a single divergence report
export const getDivergenceReportFn = createServerFn({ method: "GET" })
  .inputValidator((data: GetDivergenceReportFnParams) => data)
  .handler(async ({ data }): Promise<GetDivergenceReportResponse["data"]> => {
    const { id } = data;
    if (typeof id !== "string" || isNaN(Number(id))) {
      throw new Error("Divergence report ID must be provided as a stringified number in params.");
    }
    const config = await createAuthenticatedApiConfig();
    const response = await getDivergenceReport({
      ...config,
      path: { id: Number(id) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get divergence report", response.response?.status);
    }
    return response.data?.data || ({} as DivergenceReportReadable);
  });

export const divergenceReportsQueryOptions = (dateRange: { after: string | undefined; before: string | undefined }) => {
  return {
    queryKey: ["divergenceReports", dateRange.after, dateRange.before],
    queryFn: async () => {
      return getDivergenceReportsFn({ data: { from: dateRange.after || "", to: dateRange.before || "" } });
    },
    enabled: !!dateRange.after && !!dateRange.before,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
    refetchOnReconnect: false,
    retry: false,
  };
};
